import React, { useCallback, useMemo, useRef, useState } from "react";
import GridLayout, { verticalCompactor } from "react-grid-layout";
import type { EventCallback, Layout, LayoutItem as RGLLayoutItem } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import { Box, Typography } from "@mui/material";
import type { DroppingItem } from "components/Report-generator/context/ReportGenerator-context";
import { TableExpandProvider } from "components/Report-generator/context/TableExpandContext";
import { cardToLayoutItem } from "components/Report-generator/utils/gridUtils";
import type { LayoutItem } from "components/Report-generator/utils/gridUtils";
import type {
  CanvasCard,
  CardConfig,
  CardType,
  PageLayout,
  ReportMeta,
  ReportOrientation,
  ReportPage,
  SizePreset
} from "store/types";
import { GRID_CONSTANTS } from "store/types/reports/report-generator";

import { CardWrapper } from "../CardWrapper";
import { ReportHeader } from "./ReportHeader";
import { SectionBreakHeader } from "./SectionBreakHeader";
import { TitlePageContent } from "./TitlePageContent";

const EMPTY_SET: Set<string> = new Set();

export const PAGE_LAYOUT_OPTIONS: { value: PageLayout; label: string; tag: string }[] = [
  { value: "title", label: "Title Page", tag: "TP" },
  { value: "header-content", label: "Header + Content", tag: "HC" },
  { value: "content", label: "Content Only", tag: "CO" },
  { value: "section", label: "Section Break", tag: "SB" }
];

interface PageViewProps {
  pageIndex: number;
  pageCount: number;
  /** Original page index matching card.page — used for layout/drop callbacks. */
  originalPageIndex: number;
  page: ReportPage;
  pageCards: CanvasCard[];
  allCards: CanvasCard[];
  pageWidth: number;
  pageHeight: number;
  selectedCardId: string;
  reportMeta: ReportMeta;
  orientation: ReportOrientation;
  isResizing: boolean;
  droppingItem: DroppingItem | null;
  isContinuation?: boolean;
  expandedRows?: Record<string, Set<string>>;
  onToggleExpand?: (cardId: string, rowKey: string) => void;
  cardOffsets?: Record<string, Record<string, number>>;
  originalCardIds?: Record<string, string>;
  onResizingChange: (resizing: boolean) => void;
  onUpdateMeta: (updates: Partial<ReportMeta>) => void;
  onLogoFileChange: (file: File | null) => void;
  onSelectCard: (id: string) => void;
  onRemoveCard: (id: string) => void;
  onDuplicateCard: (id: string) => void;
  onUpdateCard: (id: string, updates: Partial<Omit<CanvasCard, "id" | "type">>) => void;
  onPresetChange: (id: string, preset: SizePreset) => void;
  onRemovePage: () => void;
  onUpdatePage: (updates: Partial<ReportPage>) => void;
  onLayoutChange: (pageIndex: number, layout: LayoutItem[]) => void;
  onDropCard: (type: CardType, pageIndex: number, gridX: number, gridY: number, resolvedLayout?: LayoutItem[]) => void;
  onOpenSettings: (id: string) => void;
}

const compactor = verticalCompactor;

export const PageView: React.FC<PageViewProps> = ({
  pageIndex,
  pageCount,
  originalPageIndex,
  page,
  pageCards,
  allCards: _allCards,
  pageWidth,
  pageHeight,
  selectedCardId,
  reportMeta,
  orientation,
  isResizing: _isResizing,
  droppingItem,
  isContinuation: isContinuationPage,
  expandedRows,
  onToggleExpand,
  cardOffsets,
  originalCardIds,
  onResizingChange: _onResizingChange,
  onUpdateMeta,
  onLogoFileChange,
  onSelectCard,
  onRemoveCard,
  onDuplicateCard,
  onUpdateCard,
  onPresetChange,
  onRemovePage,
  onUpdatePage,
  onLayoutChange,
  onDropCard,
  onOpenSettings
}) => {
  const [showLayoutPicker, setShowLayoutPicker] = useState(false);

  const layout = page.layout;
  const showHeader = layout === "header-content";
  const isTitlePage = layout === "title";
  const isSectionBreak = layout === "section";
  const readOnly = !!isContinuationPage;

  // Full page width — horizontal padding is handled by GridLayout's containerPadding
  // so the grid's drop-sensitive area extends to the page edges.
  const contentWidth = pageWidth;
  // Reserve space for header (~120px) + footer (~40px) + padding (32px)
  const gridMinHeight = pageHeight - (showHeader ? 200 : 100);

  const gridLayout: Layout = useMemo(
    () => pageCards.map((card) => ({ ...cardToLayoutItem(card), minW: 2, minH: 1 })),
    [pageCards]
  );

  // Force GridLayout to reinitialize when card heights change.
  // RGL manages an internal layout state synced via useEffect + deepEqual, but
  // this sync can miss updates (e.g. nested table row expansion changes gridH
  // without changing the child key set). A key derived from heights forces a
  // fresh mount, guaranteeing the internal state matches the props.
  const gridLayoutKey = useMemo(() => pageCards.map((c) => c.gridH).join(","), [pageCards]);

  const rglDroppingItem: RGLLayoutItem | undefined = useMemo(() => {
    if (!droppingItem) return undefined;
    return { i: droppingItem.i, x: 0, y: 0, w: droppingItem.w, h: droppingItem.h };
  }, [droppingItem]);

  // Stabilize config objects so RGL internals don't recompute on every render
  const gridConfig = useMemo(
    () => ({
      cols: GRID_CONSTANTS.GRID_COLUMNS,
      rowHeight: GRID_CONSTANTS.GRID_CELL_HEIGHT,
      margin: [GRID_CONSTANTS.GAP, GRID_CONSTANTS.GAP] as const,
      containerPadding: [28, 16] as const,
      maxRows: Infinity
    }),
    []
  );

  const dragConfig = useMemo(
    () => ({
      enabled: !readOnly,
      handle: ".rgl-drag-handle",
      bounded: false,
      threshold: 3
    }),
    [readOnly]
  );

  const resizeConfig = useMemo(() => ({ enabled: !readOnly, handles: ["se"] as const }), [readOnly]);

  const dropConfig = useMemo(
    () => ({
      enabled: !readOnly,
      defaultItem: { w: rglDroppingItem?.w ?? 6, h: rglDroppingItem?.h ?? 3 }
    }),
    [readOnly, rglDroppingItem?.w, rglDroppingItem?.h]
  );

  // Track whether a user interaction (drag/resize) is in progress.
  // We only persist layout changes from explicit user actions (dragStop, resizeStop, drop)
  // to prevent the library's automatic re-compaction from creating a feedback loop
  // where positions oscillate between two valid compacted states.
  const isInteractingRef = useRef(false);

  const persistLayout = useCallback(
    (newLayout: Layout) => {
      const items: LayoutItem[] = newLayout.map((item) => ({
        i: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h
      }));
      onLayoutChange(originalPageIndex, items);
    },
    [onLayoutChange, originalPageIndex]
  );

  // No-op for automatic compaction — positions are persisted via stop/drop handlers only
  const handleLayoutChange = useCallback((_newLayout: Layout) => {}, []);

  const handleDragStart: EventCallback = useCallback(() => {
    isInteractingRef.current = true;
  }, []);

  const handleDragStop: EventCallback = useCallback(
    (finalLayout: Layout) => {
      isInteractingRef.current = false;
      persistLayout(finalLayout);
    },
    [persistLayout]
  );

  const handleResizeStop: EventCallback = useCallback(
    (finalLayout: Layout) => {
      isInteractingRef.current = false;
      persistLayout(finalLayout);
    },
    [persistLayout]
  );

  const handleDrop = useCallback(
    (resolvedLayout: Layout, droppedItem: RGLLayoutItem | undefined, event: Event) => {
      if (!droppedItem) return;
      const cardType = (event as DragEvent).dataTransfer?.getData("cardType") as CardType | undefined;
      if (cardType) {
        const existingLayout: LayoutItem[] = resolvedLayout
          .filter((item) => item.i !== droppedItem.i)
          .map((item) => ({ i: item.i, x: item.x, y: item.y, w: item.w, h: item.h }));
        onDropCard(cardType, originalPageIndex, droppedItem.x, droppedItem.y, existingLayout);
      }
    },
    [onDropCard, originalPageIndex]
  );

  return (
    <Box sx={{ position: "relative" }}>
      {/* Page badge + layout picker */}
      <Box
        sx={{
          position: "absolute",
          top: -32,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: "6px"
        }}
      >
        <Box
          sx={{
            bgcolor: isContinuationPage ? "rgba(7,31,53,0.55)" : "#071f35",
            color: "#FFFFFF",
            fontSize: 10,
            fontWeight: 800,
            p: "3px 12px",
            borderRadius: "999px",
            letterSpacing: "0.04em"
          }}
        >
          PAGE {pageIndex + 1} OF {pageCount}
          {isContinuationPage && " (continued)"}
        </Box>
        {!isContinuationPage && (
          <Box
            onClick={() => setShowLayoutPicker((v) => !v)}
            sx={{
              bgcolor: showLayoutPicker ? "#071f35" : "rgba(7,31,53,0.08)",
              color: showLayoutPicker ? "#FFFFFF" : "#071f35",
              border: "none",
              borderRadius: "999px",
              fontSize: 10,
              fontWeight: 700,
              p: "3px 12px",
              cursor: "pointer"
            }}
          >
            {PAGE_LAYOUT_OPTIONS.find((o) => o.value === layout)?.label ?? layout}
          </Box>
        )}
      </Box>

      {showLayoutPicker && !isContinuationPage && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: "50%",
            transform: "translateX(-50%)",
            bgcolor: "#FFFFFF",
            borderRadius: "10px",
            boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
            border: "1px solid #dbe8f5",
            p: 1,
            display: "flex",
            gap: "6px",
            zIndex: 20,
            flexWrap: "wrap",
            justifyContent: "center",
            width: Math.min(pageWidth - 32, 540)
          }}
        >
          {PAGE_LAYOUT_OPTIONS.map((option) => (
            <Box
              key={option.value}
              component="button"
              onClick={() => {
                onUpdatePage({ layout: option.value });
                setShowLayoutPicker(false);
              }}
              sx={{
                p: "8px 12px",
                border: `1px solid ${option.value === layout ? "#071f35" : "#dbe8f5"}`,
                borderRadius: "8px",
                bgcolor: option.value === layout ? "#E8F5F0" : "#FAFCFE",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
                minWidth: 94
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#071f35" }}>{option.tag}</Typography>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#071f35" }}>{option.label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {pageCards.length === 0 && pageIndex > 0 && !isContinuationPage && (
        <Box
          component="button"
          onClick={onRemovePage}
          sx={{
            position: "absolute",
            top: -14,
            right: 0,
            zIndex: 5,
            bgcolor: "#FEE2E2",
            color: "#d93e3e",
            border: "none",
            borderRadius: "999px",
            fontSize: 10,
            fontWeight: 700,
            p: "4px 10px",
            cursor: "pointer"
          }}
        >
          Remove page
        </Box>
      )}

      <Box
        sx={{
          width: pageWidth,
          height: pageHeight,
          flexShrink: 0,
          bgcolor: "#FFFFFF",
          boxShadow: "0 4px 32px rgba(0,0,0,0.18), 0 1px 6px rgba(0,0,0,0.10)",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          // Only show grid lines on interactive (non-continuation) content pages
          ...(isTitlePage || isSectionBreak || isContinuationPage
            ? {}
            : {
                backgroundImage: [
                  "linear-gradient(rgba(7,31,53,0.05) 1px, transparent 1px)",
                  "linear-gradient(90deg, rgba(7,31,53,0.05) 1px, transparent 1px)"
                ].join(", "),
                backgroundSize: `${(contentWidth + GRID_CONSTANTS.GAP) / GRID_CONSTANTS.GRID_COLUMNS}px ${GRID_CONSTANTS.GRID_CELL_HEIGHT + GRID_CONSTANTS.GAP}px`
              })
        }}
        onClick={() => {
          onSelectCard("");
          setShowLayoutPicker(false);
        }}
      >
        {isTitlePage && (
          <TitlePageContent
            meta={reportMeta}
            pageHeight={pageHeight}
            onUpdate={onUpdateMeta}
            onLogoFileChange={onLogoFileChange}
          />
        )}
        {isSectionBreak && (
          <SectionBreakHeader title={page.title ?? ""} onTitleChange={(title) => onUpdatePage({ title })} />
        )}
        {showHeader && <ReportHeader meta={reportMeta} onUpdate={onUpdateMeta} onLogoFileChange={onLogoFileChange} />}

        {!isTitlePage && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              position: "relative",
              // Only show drop placeholder on interactive pages
              ...(!isContinuationPage && {
                "& .react-grid-item.react-grid-placeholder": {
                  background: "#1A3C34",
                  borderRadius: "6px",
                  opacity: 0.12
                }
              })
            }}
          >
            <GridLayout
              key={gridLayoutKey}
              layout={gridLayout}
              gridConfig={gridConfig}
              compactor={compactor}
              dropConfig={dropConfig}
              dragConfig={dragConfig}
              resizeConfig={resizeConfig}
              droppingItem={isContinuationPage ? undefined : rglDroppingItem}
              width={contentWidth}
              onLayoutChange={handleLayoutChange}
              onDragStart={handleDragStart}
              onDragStop={handleDragStop}
              onResizeStop={handleResizeStop}
              onDrop={handleDrop}
              autoSize
              style={{ minHeight: gridMinHeight }}
            >
              {pageCards.map((card) => {
                const origId = originalCardIds?.[card.id] ?? card.id;
                const isContinuationCard = originalCardIds?.[card.id] !== undefined;
                const expandValue =
                  expandedRows && onToggleExpand
                    ? {
                        expanded: expandedRows[origId] ?? EMPTY_SET,
                        onToggle: (rowKey: string) => onToggleExpand(origId, rowKey),
                        indexOffsets: cardOffsets?.[card.id]
                      }
                    : null;
                return (
                  <div key={card.id} onClick={(e) => e.stopPropagation()}>
                    <TableExpandProvider value={expandValue}>
                      <CardWrapper
                        card={card}
                        isSelected={selectedCardId === origId}
                        onSelect={() => onSelectCard(origId)}
                        onRemove={() => onRemoveCard(origId)}
                        onDuplicate={() => onDuplicateCard(origId)}
                        onOpenSettings={() => onOpenSettings(origId)}
                        onUpdateConfig={(config: CardConfig) => onUpdateCard(origId, { config })}
                        onPresetChange={(preset: SizePreset) => onPresetChange(origId, preset)}
                        readOnly={isContinuationCard}
                        orientation={orientation}
                      />
                    </TableExpandProvider>
                  </div>
                );
              })}
            </GridLayout>
            {pageCards.length === 0 && !isContinuationPage && (
              <Box
                sx={{
                  position: "absolute",
                  inset: "28px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                  border: "2px dashed rgba(7,31,53,0.2)",
                  borderRadius: "10px",
                  boxSizing: "border-box",
                  pointerEvents: "none"
                }}
              >
                <svg width="32" height="40" viewBox="0 0 32 40" fill="none">
                  <rect x="1" y="1" width="30" height="38" rx="2" stroke="rgba(7,31,53,0.28)" strokeWidth="1.5" />
                  <line
                    x1="7"
                    y1="12"
                    x2="25"
                    y2="12"
                    stroke="rgba(7,31,53,0.28)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="7"
                    y1="18"
                    x2="25"
                    y2="18"
                    stroke="rgba(7,31,53,0.28)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="7"
                    y1="24"
                    x2="18"
                    y2="24"
                    stroke="rgba(7,31,53,0.28)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#92a7bc" }}>Drag components here</Typography>
                <Typography sx={{ fontSize: 12, color: "#92a7bc" }}>
                  {PAGE_LAYOUT_OPTIONS.find((o) => o.value === layout)?.label ?? "Page"} - Page {pageIndex + 1}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Footer */}
        <Box
          sx={{
            borderTop: "1px solid #dbe8f5",
            p: "12px 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexShrink: 0,
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0
          }}
        >
          <Typography sx={{ fontSize: 10, color: "#92a7bc" }}>
            Page {pageIndex + 1} of {pageCount}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
